import { useCallback, useEffect, useState } from "react";

import {
    getOrganizationBranch,
    removeBranchPartner,
    updateBranchFeatures,
    updateBranchName,
    updateBranchPartner,
    updateBranchTariff,
} from "services/organizationBranches/rest";
import { OrganizationBranchDto } from "services/organizationBranches/restModels";
import { UpdateBranchTariffRequestDto } from "services/organizationBranches/restRequests";
import { useNotificationSnackbar } from "shared/ui/NotificationSnackbar/useNotificationSnackbar";

import { BranchDetailsTab } from "../constants";

export const useBranchDetailsState = (branchId: number) => {
    const notify = useNotificationSnackbar();

    const [branch, setBranch] = useState<OrganizationBranchDto | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [tab, setTab] = useState<BranchDetailsTab>("tariff");
    const [isRenaming, setIsRenaming] = useState(false);

    const loadBranch = useCallback(async () => {
        setLoading(true);

        const response = await getOrganizationBranch(branchId);

        setBranch(response);
        setLoading(false);
    }, [branchId]);

    useEffect(() => {
        loadBranch();
    }, [loadBranch]);

    /*
    * Тихое обновление карточки: без флага загрузки, иначе экран схлопнулся бы в скелетон и
    * потерял открытую вкладку. Нужно после действий на соседних вкладках, которые меняют
    * карточку на бэке, — оплата счёта продлевает тариф.
    * */
    const refreshBranch = useCallback(async () => {
        const response = await getOrganizationBranch(branchId);

        if (response) setBranch(response);
    }, [branchId]);

    /*
    * Мутации карточки отдают её целиком, поэтому кладём ответ в стейт вместо повторного GET.
    * */
    const save = async (
        action: () => Promise<OrganizationBranchDto>,
        successTitle: string,
        failureTitle: string
    ) => {
        setSaving(true);

        try {
            setBranch(await action());
            notify.showSuccess(successTitle);
            return true;
        } catch (caught) {
            notify.showError(failureTitle, caught);
            return false;
        } finally {
            setSaving(false);
        }
    };

    const handleRename = async (name: string) => {
        const saved = await save(
            () => updateBranchName(branchId, { name }),
            "Название обновлено",
            "Не удалось переименовать заведение"
        );

        if (saved) setIsRenaming(false);
    };

    const handleSaveTariff = (tariff: UpdateBranchTariffRequestDto) =>
        save(
            () => updateBranchTariff(branchId, tariff),
            "Тариф обновлён",
            "Не удалось сохранить тариф"
        );

    const handleSaveFeatures = (isOldPriceLabelEnabled: boolean) =>
        save(
            () => updateBranchFeatures(branchId, { isOldPriceLabelEnabled }),
            "Функции обновлены",
            "Не удалось сохранить функции"
        );

    const handleSavePartner = (partnerId: number) =>
        save(
            () => updateBranchPartner(branchId, { partnerId }),
            "Партнёр назначен",
            "Не удалось назначить партнёра"
        );

    const handleRemovePartner = () =>
        save(
            () => removeBranchPartner(branchId),
            "Партнёр снят",
            "Не удалось снять партнёра"
        );

    return {
        branch,
        loading,
        saving,
        tab,
        isRenaming,
        notify,
        onChangeTab: setTab,
        onStartRenaming: () => setIsRenaming(true),
        onCancelRenaming: () => setIsRenaming(false),
        onRename: handleRename,
        onSaveTariff: handleSaveTariff,
        onSaveFeatures: handleSaveFeatures,
        onSavePartner: handleSavePartner,
        onRemovePartner: handleRemovePartner,
        onRetry: loadBranch,
        refreshBranch,
    };
};
